import GlobalModal from 'components/globalModal';
import DefaultAvatar from 'assets/images/default-avatar.png';
import { DatePicker, Form, Input } from 'antd';
import { DATE_FORMAT, ROLE_TYPES } from 'api/constants';
import { COLORS } from 'utils/colors';
import { IconCalendar } from 'components/icons/Calendar';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { FormInput, IconSmallArrow, SelectOption } from 'components';
import { getRequestData, terminateClient } from 'api/api';
import { endpoints } from 'api/endpoints';
import classNames from 'classnames';
import { useUser } from 'context/userContext';
import { formatDateAsUtcStartOfDay } from 'utils/helpers';

const defaultTerminationReasons = [
  { id: 1, reason: 'Violation of the rules' },
  { id: 2, reason: "Client's choice" },
  { id: 3, reason: 'Early Recovery' },
  { id: 4, reason: 'Return to Use' },
  { id: 5, reason: 'Rules Violation' },
  { id: 6, reason: 'Secured Other Funding' },
  { id: 7, reason: 'Other' },
];

export const Terminate = ({ isOpen, onClose, userDetails, onSuccess, isAdmin }) => {
  const [terminationReasons, setTerminationReasons] = useState([]);
  const { updateUserDetails } = useUser();
  const [formData, setFormData] = useState({
    partner_id: userDetails.client.partner_id,
    reason_id: undefined,
    reason: '',
    date: '',
    reason_details: '',
  });

  const isTerminated = useMemo(
    () => Boolean(userDetails.client.termination_date),
    [userDetails.client.termination_date],
  );
  const domain = isAdmin ? ROLE_TYPES.admin : ROLE_TYPES.slh_manager;

  useEffect(() => {
    (async () => {
      const response = await getRequestData(endpoints.getTerminationReasons, domain);

      if (response?.data) {
        const reasons = response.data.map((i) => ({ label: i.reason, value: i.id }));
        setTerminationReasons(
          reasons.length
            ? reasons
            : defaultTerminationReasons.map((i) => ({ label: i.reason, value: i.id })),
        );
      }
    })();
  }, [domain]);

  // initialize formData from userDetails (including already-terminated model)
  useEffect(() => {
    const client = userDetails?.client || {};
    const reasonIdRaw =
      client.reason_id ?? client.termination_reason_id ?? client.termination_reason_id;
    const reasonText = client.reason ?? client.termination_reason ?? client.termination_reason;
    const date = client.date ?? client.termination_date ?? client.termination_date;
    const reasonDetails =
      client.reason_details ??
      client.termination_reason_details ??
      client.termination_reason_details;

    setFormData((prev) => ({
      ...prev,
      partner_id: client.partner_id ?? prev.partner_id,
      reason_id: reasonIdRaw != null ? Number(reasonIdRaw) : prev.reason_id,
      reason: reasonText ?? prev.reason,
      date: date ?? prev.date,
      reason_details: reasonDetails ?? prev.reason_details,
    }));
  }, [userDetails]);

  const handleTerminationDateChange = (date) => {
    const terminationDate = formatDateAsUtcStartOfDay(date);
    setFormData({
      ...formData,
      date: terminationDate,
    });
  };

  const handleTerminationReasonSelect = (value) => {
    // value is reason id
    const val = value == null ? undefined : Number(value);
    const selected = terminationReasons.find((r) => r.value === val);
    setFormData({
      ...formData,
      reason_id: val,
      reason: selected ? selected.label : formData.reason,
    });
  };

  const handleTextAreaChange = (e) => {
    setFormData({
      ...formData,
      reason_details: e.target.value,
    });
  };
  const handleFormSubmit = () => {
    terminateClient(userDetails.profile.user_id, formData, domain, () => {
      onSuccess(onClose);
      updateUserDetails();
    });
  };

  return (
    <GlobalModal open={isOpen} title="End of Scholarship" onClose={onClose} titleLeft>
      <Form name="terminate_form" className="flex flex-col p-[30px]" onFinish={handleFormSubmit}>
        <div className="flex justify-between">
          <div className="flex">
            <img
              className="w-12 h-12 rounded-xl"
              src={userDetails.profile.avatar ? userDetails.profile.avatar : DefaultAvatar}
              alt="profile"
            />
            <div className="flex flex-col ml-3">
              <b className="text-black text-base nunito">
                {userDetails.profile.firstname} {userDetails.profile.lastname}
              </b>
              <p className="text-lightBlue text-14 font-normal mt-0.5">{userDetails.client.name}</p>
            </div>
          </div>
          <div className="flex flex-col gap-y-1 w-full max-w-[200px]">
            <p className="text-14 font-medium">End of Scholarship Date</p>
            <DatePicker
              allowClear={false}
              onChange={(_, date) => handleTerminationDateChange(date)}
              format={DATE_FORMAT}
              placeholder="Pick Date"
              className={classNames(
                'h-[44px] text-base !text-black font-medium base-block rounded-lg',
                { '!text-black !bg-white !shadow-block-shadow': isTerminated },
              )}
              name="date-of-born"
              defaultValue={isTerminated ? dayjs(userDetails.client.termination_date) : null}
              suffixIcon={!isTerminated && <IconCalendar color={COLORS.blue} />}
              disabled={isTerminated}
            />
          </div>
        </div>
        <div className="flex flex-col w-full gap-y-1 mt-6">
          <p className="text-14 font-medium">Reason of Scholarship End</p>
          <SelectOption
            onChange={(value) => handleTerminationReasonSelect(value)}
            dropdownStyle={{ padding: '16px', background: 'white' }}
            disabled={isTerminated}
            suffixIcon={<IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />}
            className={classNames('h-[44px] text-base font-medium base-block rounded-lg', {
              '!text-black !bg-white !shadow-block-shadow': isTerminated,
            })}
            placeholder={
              isTerminated
                ? (userDetails.client.reason ??
                  userDetails.client.termination_reason ??
                  'Select Reason')
                : 'Select Reason'
            }
            value={formData.reason_id}
            dropdownRender={(originalNode) => originalNode}
            optionRender={(option) => (
              <div
                className={`flex items-center px-2 py-1 gap-x-1 text-black rounded-md capitalize border border-color-divider bg-white overflow-hidden ${Number(formData.reason_id) === option.value ? 'border-orange' : ''}`}
              >
                <span
                  className={`w-6 h-6 rounded-full border border-orange ${Number(formData.reason_id) === option.value ? 'border-[6px]' : ''}`}
                />
                <p className="font-14">{option.label}</p>
              </div>
            )}
            options={terminationReasons}
          />
        </div>
        {Number(formData.reason_id) === 7 && (
          <FormInput
            label="Other Reason"
            name="reason"
            className="mt-2"
            placeholder="Type the reason"
            required
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            defaultValue={
              isTerminated
                ? (userDetails.client.reason ?? userDetails.client.termination_reason)
                : undefined
            }
            {...(isTerminated ? { disabled: true } : {})}
          />
        )}
        <div className="flex flex-col gap-y-1 w-full mt-6">
          <p className="text-14 font-medium">Additional Comments</p>
          <Input.TextArea
            className="!resize-none font-poppins text-14 p-3 pl-4 border border-color-divider rounded-ten max-h-[200px]"
            autoSize={false}
            rows={5}
            placeholder="Comment"
            onChange={handleTextAreaChange}
            defaultValue={userDetails.client.reason_details}
            readOnly={isTerminated}
            disabled={isTerminated}
          />
        </div>
        {!isTerminated && (
          <button
            type="submit"
            className="px-6 py-2.5 bg-transparent border border-red rounded-[10px] mt-6 self-center text-red text-16 font-medium duration-150 hover:border-orange hover:text-orange"
          >
            End Scholarship
          </button>
        )}
      </Form>
    </GlobalModal>
  );
};
